/* eslint-disable react-hooks/exhaustive-deps */
import { RotateCw, Search, Copy } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../atoms/RowEmpty'
import TableAction from '../../atoms/Table/TableAction'
import TablePagination from '../../atoms/Table/TablePagination'
import InputDatePicker from '../../../components/Input/DatePicker/InputDatePicker'
import api from '../../../config/AxiosInstance'
import { convertDateWithTime } from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const SelectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: '10px',
    border: '1px solid #e9ecef',
    boxShadow: 'none',
    minHeight: '45px',
    fontSize: '0.9rem',
    '&:hover': {
      border: '1px solid #bdc3c7'
    }
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '10px',
    fontSize: '0.9rem',
    zIndex: 9999
  })
}

const DELIVERY_TYPES = [
  { value: 'pick_up', label: 'Pick Up' },
  { value: 'sewa', label: 'Sewa' },
  { value: 'internal', label: 'Internal' }
]

const OutboundHistory = () => {
  const navigate = useNavigate()
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [loading, setLoading] = useState(false)
  const [outboundHistories, setOutboundHistories] = useState([])
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const [warehouses, setWarehouses] = useState([])
  const [filter, setFilter] = useState({
    outbound: '',
    document: '',
    warehouse_id: '',
    delivery_type: '',
    start_date: '',
    end_date: ''
  })
  const [debouncedOutbound, setDebouncedOutbound] = useState(filter.outbound)
  const [debouncedDocument, setDebouncedDocument] = useState(filter.document)

  const deliveryTypeOptions = [
    { value: '', label: 'All Delivery Types' },
    ...DELIVERY_TYPES.map((s) => ({ value: s.value, label: s.label }))
  ]

  const warehouseOptions = [
    { value: '', label: 'All Warehouses' },
    ...warehouses.map((w) => ({ value: w.id, label: w.warehouse_name }))
  ]

  const fetchOutboundHistories = async () => {
    setLoading(true)
    try {
      const params = {
        per_page: meta.per_page,
        page: meta.current_page,
        outbound: debouncedOutbound,
        document: debouncedDocument,
        warehouse_id: filter.warehouse_id,
        delivery_type: filter.delivery_type,
        start_date: filter.start_date
          ? convertDateWithTime(filter.start_date, 'YYYY-MM-DD')
          : '',
        end_date: filter.end_date
          ? convertDateWithTime(filter.end_date, 'YYYY-MM-DD')
          : ''
      }

      const res = await api.get('outbound/history/list', { params })
      const { metadata, data } = res.data
      setOutboundHistories(data)
      setMeta(metadata)
    } catch (err) {
      if (err?.response?.status !== 404) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      } else {
        setOutboundHistories([])
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchWarehouses = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      setWarehouses(data)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  const handleMetaChange = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: parseInt(data.selected + 1)
    }))
    window.scrollTo(0, 0)
  }

  const handleFilterChange = (name, value) => {
    let newFilter = {
      ...filter,
      [name]: value
    }

    if (name === 'start_date' && value && filter.end_date) {
      if (new Date(value) > new Date(filter.end_date)) {
        newFilter.end_date = value
      }
    }

    if (name === 'end_date' && value && filter.start_date) {
      if (new Date(value) < new Date(filter.start_date)) {
        newFilter.start_date = value
      }
    }

    setFilter(newFilter)

    if (name !== 'outbound' && name !== 'document') {
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }
  }

  const handleReset = () => {
    setFilter({
      outbound: '',
      document: '',
      warehouse_id: '',
      delivery_type: '',
      start_date: '',
      end_date: ''
    })
    setDebouncedOutbound('')
    setDebouncedDocument('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const navigateToDetail = (data) => {
    if (data?.type === 'sj') {
      return `/outbound/sj/detail/${encodeURIComponent(data.erp_doc_no)}`
    }
    if (data?.type === 'tr') {
      return `/outbound/transfer/detail/${data.sj_draft_id}`
    }
    if (data?.type === 'rr') {
      return `/outbound/request-return/detail/${encodeURIComponent(
        data.source_doc_no
      )}`
    }
    return '#'
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedOutbound(filter.outbound)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [filter.outbound])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDocument(filter.document)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [filter.document])

  useEffect(() => {
    const isDateValid =
      (!filter.start_date && !filter.end_date) ||
      (filter.start_date && filter.end_date)

    if (isDateValid) {
      fetchOutboundHistories()
    }
  }, [
    meta.per_page,
    meta.current_page,
    debouncedOutbound,
    debouncedDocument,
    filter.warehouse_id,
    filter.delivery_type,
    filter.start_date,
    filter.end_date
  ])

  useEffect(() => {
    fetchWarehouses()
  }, [])

  return (
    <Container fluid className="px-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Outbound History</h3>
        </Col>
        <Col className="text-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem', height: '37px' }}
          >
            <RotateCw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Outbound No</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={16} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Outbound no"
                  value={filter.outbound}
                  onChange={(e) =>
                    handleFilterChange('outbound', e.target.value)
                  }
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>SJ/TR/RR</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={16} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Ticket no"
                  value={filter.document}
                  onChange={(e) =>
                    handleFilterChange('document', e.target.value)
                  }
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                value={warehouseOptions.find(
                  (o) => o.value === filter.warehouse_id
                )}
                onChange={(e) =>
                  handleFilterChange('warehouse_id', e?.value || '')
                }
                options={warehouseOptions}
                styles={SelectStyles}
                placeholder="All Warehouses"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Delivery Type</Form.Label>
              <Select
                value={deliveryTypeOptions.find(
                  (o) => o.value === filter.delivery_type
                )}
                onChange={(e) =>
                  handleFilterChange('delivery_type', e?.value || '')
                }
                options={deliveryTypeOptions}
                styles={SelectStyles}
                placeholder="All Types"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Start Date</Form.Label>
              <div
                style={FormControlStyle}
                className="d-flex align-items-center px-2"
              >
                <InputDatePicker
                  value={filter.start_date}
                  onChange={(date) => handleFilterChange('start_date', date)}
                  maxDate={filter.end_date ? new Date(filter.end_date) : null}
                  clearIcon
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>End Date</Form.Label>
              <div
                style={FormControlStyle}
                className="d-flex align-items-center px-2"
              >
                <InputDatePicker
                  value={filter.end_date}
                  onChange={(date) => handleFilterChange('end_date', date)}
                  minDate={
                    filter.start_date ? new Date(filter.start_date) : null
                  }
                  clearIcon
                />
              </div>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row className="mt-2">
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Outbound No.
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Warehouse
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Source Doc No.
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  ERP Doc No.
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Delivery Type
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Type
                </th>
                <th
                  className="p-2 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Submit
                </th>
                <th
                  className="p-2 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center p-5">
                    <Loader />
                  </td>
                </tr>
              ) : outboundHistories.length > 0 ? (
                outboundHistories.map((data, i) => (
                  <tr key={i}>
                    <td className="p-2 align-middle">
                      <a
                        className="fw-bold text-decoration-none"
                        href={`/outbound/plan/detail/${data.outbound_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {data?.outbound_number || '-'}
                      </a>
                    </td>
                    <td className="p-2 align-middle">
                      {data?.warehouse_name || '-'}
                    </td>
                    <td className="p-2 align-middle">
                      <div className="d-flex align-items-center gap-2">
                        {data?.source_doc_no || '-'}
                        {data?.source_doc_no && (
                          <Copy
                            size={14}
                            className="text-muted cursor-pointer"
                            style={{
                              cursor: 'pointer',
                              opacity: 0.5,
                              transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.opacity = 1)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.opacity = 0.5)
                            }
                            onClick={() => handleCopy(data?.source_doc_no)}
                            title="Copy Source Doc Number"
                          />
                        )}
                      </div>
                    </td>
                    <td className="p-2 align-middle">
                      <div className="d-flex align-items-center gap-2">
                        {data?.erp_doc_no || '-'}
                        {data?.erp_doc_no && (
                          <Copy
                            size={14}
                            className="text-muted cursor-pointer"
                            style={{
                              cursor: 'pointer',
                              opacity: 0.5,
                              transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.opacity = 1)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.opacity = 0.5)
                            }
                            onClick={() => handleCopy(data?.erp_doc_no)}
                            title="Copy ERP Doc Number"
                          />
                        )}
                      </div>
                    </td>
                    <td className="p-2 align-middle text-uppercase fw-bold">
                      {data?.delivery_type || '-'}
                    </td>
                    <td className="p-2 align-middle text-uppercase fw-bold">
                      {data?.type || '-'}
                    </td>
                    <td className="p-2 align-middle text-end">
                      {data?.submit ? convertDateWithTime(data.submit) : '-'}
                    </td>
                    <td className="p-2 align-middle text-end">
                      <TableAction>
                        <button
                          className="btn btn-light text-secondary border rounded shadow-sm"
                          style={{
                            width: '35px',
                            height: '35px',
                            padding: 0
                          }}
                          onClick={() =>
                            navigate(navigateToDetail(data), { replace: true })
                          }
                        >
                          <Search size={16} />
                        </button>
                      </TableAction>
                    </td>
                  </tr>
                ))
              ) : (
                <RowEmpty colSpan={8} />
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={8} className="p-3 border-top-0">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleMetaChange}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>
      <Toaster />
    </Container>
  )
}

export default OutboundHistory
